import React from 'react'
import { useContext, useState, createContext, ReactNode } from 'react'
import { FormContentsModel, PerfectStoreTagsModel, StepsTypeModel } from '../models/StoreDetailModel'
import { FORM_TYPES } from '../constants/constants.stores'

interface FormContextValue {
    formsContext: FormProps[]
    setFormsContext: React.Dispatch<React.SetStateAction<FormProps[]>>
    reload: number
    setReloadChange: () => void
    updateProgressForm: (formsContext: FormProps[], schedule_id: string, form_id: string, store_id: string, progress: number) => FormProps[]
    updateStatusForm: (formsContext: FormProps[], schedule_id: string, form_id: string, store_id: string, status: number) => void
    updateStatusProgressForm: (
        formsContext: FormProps[],
        schedule_id: string,
        form_id: string,
        store_id: string,
        status: number,
        progress: number,
    ) => void
    updateStatusQuestionForm: (
        formsContext: FormProps[],
        question_id: string,
        schedule_id: string,
        form_id: string,
        store_id: string,
        status: number,
    ) => void
    setCurrentCheckin: React.Dispatch<React.SetStateAction<string | null>>
    currentCheckin: string | null
}
interface FormContextProviderProps {
    children: ReactNode
}

export interface FormProps {
    _id: string
    icon?: string
    type_form?: StepsTypeModel
    check_in?: boolean
    name_form?: string
    icon_check?: string
    contents?: FormContentsModel[]
    repeat?: boolean
    can_repeat?: boolean
    complete?: boolean
    status?: number
    store_id?: string
    schedule_id?: string
    scoreObtained?: number
    progress?: boolean
    cant_questions_yes_na?: number
    progress_questions_yes_na?: number
    show_barcode?: boolean
    mandatory?: boolean
    featured?: boolean
    tags?: PerfectStoreTagsModel[]
    question_id?: string
    refrigerator_id?: string
    link?: string
    form_id_fqa?: string
    checkin_id?: string
    // Algunos flujos marcan formularios como de objetivo
    objective?: number
    sub_title?: string
    description?: string
}

const FormContext = createContext<FormContextValue | undefined>(undefined)

export const FormContextProvider = ({ children }: FormContextProviderProps) => {
    const [formsContext, setFormsContextNew] = useState<FormProps[]>([])
    const [reload, setReload] = useState(0)
    const [currentCheckin, setCurrentCheckin] = useState(null)

    const setFormsContext = (newForms) => {
        try {
            // Debug: registrar invocaciones para diagnóstico
            try {
                const incomingCount = Array.isArray(newForms) ? newForms.length : typeof newForms
                // console.log('[DEBUG] setFormsContext called - incomingCount:', incomingCount)
            } catch (err) {}
            // Si newForms es una función (updater), delegar al set de React
            if (typeof newForms === 'function') {
                setFormsContextNew(newForms)
                return
            }

            // ✅ CRÍTICO: Si newForms es un array vacío, limpiar sin hacer merge
            if (Array.isArray(newForms) && newForms.length === 0) {
                console.log('[DEBUG] setFormsContext - Limpiando (array vacío)')
                setFormsContextNew([])
                return
            }

            // Si no hay forms previos, setear directamente
            if (!formsContext || formsContext.length === 0) {
                if (JSON.stringify(formsContext) !== JSON.stringify(newForms)) {
                    setFormsContextNew(newForms)
                }
                return
            }

            // Merge: preservar status y progress de formsContext existentes
            // Además, fusionar los arrays `contents` (formularios hijo) para no perder heladeras/entradas locales
            if (Array.isArray(newForms) && newForms.length > 0) {
                try {
                    // Map de existentes por _id para búsquedas rápidas
                    const existingMap = new Map(formsContext.map((f) => [f._id, f]))

                    const merged = newForms.map((nf) => {
                        try {
                            const exist = existingMap.get(nf._id)
                            if (exist) {
                                // Merge de contents: mantener las preguntas/childs existentes cuando corresponda
                                const existingContents = Array.isArray(exist.contents) ? exist.contents : []
                                const existingContentMap = new Map(existingContents.map((c) => [c._id, c]))

                                const nfContents = Array.isArray(nf.contents) ? nf.contents : []
                                // Para cada contenido del servidor, si existe en local, combinar (priorizar campos locales importantes)
                                const mergedContents = nfContents.map((nc) => {
                                    const ec = existingContentMap.get(nc._id)
                                    if (ec) {
                                        // preferir campos locales que representan estado (ej: active, questionInput, disabled)
                                        return { ...nc, ...ec }
                                    }
                                    return nc
                                })

                                // Añadir contenidos que existan sólo en local y no vengan del servidor (p. ej. heladeras creadas localmente)
                                existingContents.forEach((ec) => {
                                    if (!nfContents.find((nc) => nc._id === ec._id)) {
                                        mergedContents.push(ec)
                                    }
                                })

                                return {
                                    ...nf,
                                    status: exist.status !== undefined && exist.status !== null ? exist.status : nf.status,
                                    progress_questions_yes_na:
                                        exist.progress_questions_yes_na !== undefined && exist.progress_questions_yes_na !== null
                                            ? exist.progress_questions_yes_na
                                            : nf.progress_questions_yes_na,
                                    name_form: nf.name_form !== undefined && nf.name_form !== null ? nf.name_form : exist.name_form,
                                    contents: mergedContents,
                                }
                            }
                            return nf
                        } catch (err) {
                            return nf
                        }
                    })

                    // Asegurar no perder formularios que solo existen localmente: añadirlos al final
                    formsContext.forEach((ef) => {
                        if (!merged.find((m) => m._id === ef._id)) {
                            merged.push(ef)
                        }
                    })

                    if (JSON.stringify(formsContext) !== JSON.stringify(merged)) {
                        try {
                            console.log('[DEBUG] setFormsContext - applying merged result, mergedCount:', merged.length)
                        } catch (err) {}
                        setFormsContextNew(merged)
                    }
                    return
                } catch (err) {
                    // en caso de error durante merge, fallback al comportamiento anterior
                    if (JSON.stringify(formsContext) !== JSON.stringify(newForms)) {
                        try {
                            console.log('[DEBUG] setFormsContext - merge failed, applying incoming newForms, incomingCount:', Array.isArray(newForms)? newForms.length: 0)
                        } catch (err) {}
                        setFormsContextNew(newForms)
                    }
                    return
                }
            }

            // Fallback
            if (JSON.stringify(formsContext) !== JSON.stringify(newForms)) {
                setFormsContextNew(newForms)
            }
        } catch (error) {
            // si algo falla, mantener comportamiento anterior
            if (JSON.stringify(formsContext) !== JSON.stringify(newForms)) {
                setFormsContextNew(newForms)
            }
        }
    }

    const setReloadChange = () => {
        setReload((prevState) => prevState + 1)
    }

    /**
     * @description cada vez updateo la key progress_questions_yes_na progreso de un formulario
     */
    const updateProgressForm = (formsContext: FormProps[], schedule_id: string, form_id: string, store_id: string, progress: number): FormProps[] => {
        if (progress === undefined || progress === null) return null

        const indexForm = formsContext.findIndex(
            (formContext: FormProps) => formContext._id === form_id && formContext.schedule_id === schedule_id && formContext.store_id === store_id,
        )

        if (indexForm !== -1 && formsContext[indexForm].type_form.type !== FORM_TYPES.FORM_POP_HELADERA) {
            const newFormsContext = [...formsContext] // crear una copia del estado original
            newFormsContext[indexForm].progress_questions_yes_na = progress // actualizar la copia
            setFormsContext(newFormsContext) // actualizar el estado con la copia actualizada
            setReloadChange()
            return newFormsContext
        } else {
            return formsContext
        }
    }

    /**
     * @description updatea la key status de un formulario
     */
    const updateStatusForm = (formsContext: FormProps[], schedule_id: string, form_id: string, store_id: string, status: number) => {
        if (status === undefined || status === null) return

        const indexForm = formsContext.findIndex(
            (formContext: FormProps) => formContext._id === form_id && formContext.schedule_id === schedule_id && formContext.store_id === store_id,
        )

        if (indexForm !== -1 && formsContext[indexForm].type_form.type !== FORM_TYPES.FORM_POP_HELADERA) {
            const newFormsContext = [...formsContext] // crear una copia del estado original
            newFormsContext[indexForm].status = status // actualizar la copia
            setFormsContext(newFormsContext) // actualizar el estado con la copia actualizada
        }
    }

    const updateStatusProgressForm = (
        formsContext: FormProps[],
        schedule_id: string,
        form_id: string,
        store_id: string,
        status: number,
        progress: number,
    ) => {
        // console.log('🔄 [updateStatusProgressForm] Llamado con:', { 
        //     form_id, 
        //     schedule_id, 
        //     store_id, 
        //     status, 
        //     progress,
        //     formsContextLength: formsContext.length 
        // })

        const indexForm = formsContext.findIndex(
            (formContext: FormProps) => formContext._id === form_id && formContext.schedule_id === schedule_id && formContext.store_id === store_id,
        )

        // console.log('🔍 [updateStatusProgressForm] Index encontrado:', indexForm)

        if (indexForm !== -1 && formsContext[indexForm].type_form.type !== FORM_TYPES.FORM_POP_HELADERA) {
            const newFormsContext = [...formsContext] // crear una copia del estado original
            
            // console.log('📝 [updateStatusProgressForm] Valores antes de actualizar:', {
            //     oldStatus: newFormsContext[indexForm].status,
            //     oldProgress: newFormsContext[indexForm].progress_questions_yes_na,
            // })

            if (status !== undefined && status !== null) {
                newFormsContext[indexForm].status = status // actualizar la copia
            }

            if (progress !== undefined && progress !== null) {
                newFormsContext[indexForm].progress_questions_yes_na = progress // actualizar la copia
            }

            // console.log('✅ [updateStatusProgressForm] Valores después de actualizar:', {
            //     newStatus: newFormsContext[indexForm].status,
            //     newProgress: newFormsContext[indexForm].progress_questions_yes_na,
            // })

            setFormsContext(newFormsContext) // actualizar el estado con la copia actualizada
            setReloadChange()
            return newFormsContext
        } else {
            console.log('⚠️ [updateStatusProgressForm] No se actualizó - index:', indexForm, 'type:', formsContext[indexForm]?.type_form?.type)
            return formsContext
        }
    }

    /**
     * @description se agrega la key disabled con el status que le llega por param
     * @param formsContext
     * @param question_id
     * @param schedule_id
     * @param form_id
     * @param store_id
     * @param status
     */
    const updateStatusQuestionForm = (
        formsContext: FormProps[],
        question_id: string,
        schedule_id: string,
        form_id: string,
        store_id: string,
        status: number,
    ) => {
        const indexForm = formsContext.findIndex(
            (formContext: FormProps) => formContext._id === form_id && formContext.schedule_id === schedule_id && formContext.store_id === store_id,
        )

        if (indexForm !== -1) {
            const newFormsContext = [...formsContext] // crear una copia del estado original

            newFormsContext[indexForm]['contents'][question_id]['disabled'] = status

            setFormsContext(newFormsContext) // actualizar el estado con la copia actualizada
        } else {
            // console.log('question_id: ', question_id)
            // console.log('schedule_id: ', schedule_id)
            // console.log('form_id: ', form_id)
            // console.log('store_id: ', store_id)
            // console.log('status: ', status)
        }
    }

    const value: FormContextValue = {
        formsContext,
        setFormsContext,
        reload,
        setReloadChange,
        updateProgressForm,
        updateStatusForm,
        updateStatusQuestionForm,
        setCurrentCheckin,
        currentCheckin,
        updateStatusProgressForm,
    }

    return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}

export const useFormContext = (): FormContextValue => {
    const context = useContext(FormContext)
    if (!context) {
        throw new Error('useFormContext must be used within a FormContextProvider')
    }
    return context
}

//* -------------

interface FormRefrigeratorContextValue {
    set_forms_refrigerators: React.Dispatch<React.SetStateAction<FormProps[]>>
    set_form_refrigerators_main: React.Dispatch<React.SetStateAction<FormProps[]>>
    mergeAndSetFormRefrigeratorsMain: (incoming: FormProps[]) => void

    forms_refrigerators: FormProps[]
    form_refrigerators_main: FormProps[]
    getFormRefrigerator: (arrayRefrigerator: FormProps[], formId: string) => { form: FormProps | null; formQuestions: FormContentsModel[] | null }
    updateActveFormRefrigerator: (arrayRefrigerator: FormProps[], formId: string, questionId: string, status: boolean, index?: number) => void

    deleteQuestionForm: (arrayRefrigerator: FormProps[], formId: string, questionId: string) => void

    reloadActive: boolean
}

const FormRefrigeratorContext = createContext<FormRefrigeratorContextValue | undefined>(undefined)

export const FormRefrigeratorContextProvider = ({ children }: FormContextProviderProps) => {
    const [forms_refrigerators, set_forms_refrigerators] = useState<FormProps[]>([])
    const [form_refrigerators_main, set_form_refrigerators_main] = useState<FormProps[]>([])
    const [questionId, setQuestionId] = useState<null | string>(null)
    const [reloadActive, setReloadActive] = useState(false)

    const getFormRefrigerator = (arrayRefrigerator: FormProps[], formId: string) => {
        // console.log('======> getFormRefrigerator formId: ', formId)
        const findIndex = arrayRefrigerator.findIndex((form) => form._id === formId)

        let form = {}
        let formQuestions = {}
        if (findIndex !== -1) {
            form = arrayRefrigerator[findIndex]
            formQuestions = arrayRefrigerator[findIndex]['contents']
        }

        return {
            form: form as FormProps,
            formQuestions: formQuestions as FormContentsModel[],
        }
    }

    const updateActveFormRefrigerator = (arrayRefrigerator: FormProps[], formId: string, questionId: string, status: boolean, index?: number) => {
        const newFormsRefrigeratorContext = [...arrayRefrigerator]

        const formIndex = newFormsRefrigeratorContext.findIndex((form) => form._id === formId)

        if (formIndex !== -1) {
            const form = newFormsRefrigeratorContext[formIndex]
            let formQuestions = [...form.contents]

            const questionIndex = formQuestions.findIndex((quest) => quest._id === questionId)

            if (questionIndex !== -1) {
                formQuestions[questionIndex] = {
                    ...formQuestions[questionIndex],
                    active: status,
                    index: index,
                }

                newFormsRefrigeratorContext[formIndex] = {
                    ...form,
                    contents: formQuestions,
                }
            } else {
                console.log('no encontre pregunta: questionId: ', questionId)
            }
        } else {
            console.log('No encontre formulario:  formId: ', formId)
        }

        set_form_refrigerators_main(newFormsRefrigeratorContext)
        setReloadActive(!reloadActive)
    }

    const deleteQuestionForm = (arrayRefrigerator: FormProps[], formId: string, questionId: string) => {
        const newFormsRefrigeratorContext = [...arrayRefrigerator]
        const formIndex = newFormsRefrigeratorContext.findIndex((form) => form._id === formId)

        if (formIndex !== -1) {
            const form = newFormsRefrigeratorContext[formIndex]
            let formQuestions = [...form.contents]

            const questionIndex = formQuestions.findIndex((quest) => quest._id === questionId)

            if (questionIndex !== -1) {
                formQuestions = formQuestions.filter((quest) => quest._id !== questionId)

                console.log('258 ----- formQuestions: ', JSON.stringify(formQuestions))

                newFormsRefrigeratorContext[formIndex] = {
                    ...form,
                    contents: formQuestions,
                }
            } else {
                console.log('no encontre pregunta: questionId: ', questionId)
            }
        } else {
            console.log('No encontre formulario:  formId: ', formId)
        }

        set_form_refrigerators_main(newFormsRefrigeratorContext)
        setReloadActive(!reloadActive)
    }

    const mergeAndSetFormRefrigeratorsMain = (incoming: FormProps[]) => {
        try {
            const existing = form_refrigerators_main || []

            const existingMap = new Map(existing.map((f) => [f._id, f]))

            const merged = incoming.map((incForm) => {
                const extForm = existingMap.get(incForm._id)
                if (!extForm) return incForm

                const existingContentMap = new Map((extForm.contents || []).map((c) => [c._id, c]))

                const incContents = (incForm.contents || []).map((c) => {
                    const extC = existingContentMap.get(c._id)
                    if (!extC) return c
                    return {
                        ...c,
                        ...(extC.active !== undefined ? { active: extC.active } : {}),
                        ...(extC.index !== undefined ? { index: extC.index } : {}),
                    }
                })

                ;(extForm.contents || []).forEach((ec) => {
                    if (!incContents.find((ic) => ic._id === ec._id)) {
                        incContents.push(ec)
                    }
                })

                return {
                    ...incForm,
                    contents: incContents,
                }
            })

            existing.forEach((ef) => {
                if (!merged.find((m) => m._id === ef._id)) merged.push(ef)
            })

            set_form_refrigerators_main(merged)
            setReloadActive(!reloadActive)
        } catch (error) {
            // fallback
            set_form_refrigerators_main(incoming)
            setReloadActive(!reloadActive)
        }
    }

    const value: FormRefrigeratorContextValue = {
        forms_refrigerators,
        set_forms_refrigerators,
        form_refrigerators_main,
        set_form_refrigerators_main,
        mergeAndSetFormRefrigeratorsMain,
        getFormRefrigerator,
        updateActveFormRefrigerator,
        reloadActive,
        deleteQuestionForm,
    }

    return <FormRefrigeratorContext.Provider value={value}>{children}</FormRefrigeratorContext.Provider>
}

export const useFormRefrigeratorContext = (): FormRefrigeratorContextValue => {
    const context = useContext(FormRefrigeratorContext)
    if (!context) {
        throw new Error('useFormRefrigeratorContext must be used within a useFormRefrigeratorContext')
    }
    return context
}
